import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../../services/Axios';
import Loader from '../../utils/loader/Loader';
import {
  FaCashRegister,
  FaFilter,
  FaEye,
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaMoneyBillWave,
  FaUser,
} from 'react-icons/fa';

interface CashRegister {
  _id: string;
  montoInicial: number;
  montoEsperado: number;
  montoReal?: number;
  diferencia?: number;
  estado: 'abierta' | 'cerrada';
  fechaApertura: string;
  fechaCierre?: string;
  sucursal: {
    nombre: string;
  };
  cajero: {
    nombre: string;
    email: string;
  };
  ventas: {
    total: number;
    cantidad: number;
  };
  notas?: string;
}

const CashRegisterHistory: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [cajas, setCajas] = useState<CashRegister[]>([]);
  const [selectedCaja, setSelectedCaja] = useState<CashRegister | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    estado: '',
    startDate: '',
    endDate: '',
  });

  // Stats
  const [totalCajas, setTotalCajas] = useState(0);
  const [cajasAbiertas, setCajasAbiertas] = useState(0);
  const [totalVentas, setTotalVentas] = useState(0);

  const loadStats = useCallback(async () => {
    try {
        const params: any = {};

        if (filters.estado) params.estado = filters.estado;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;

        const response = await api.get('/api/cash-register/stats', { params });
        setTotalCajas(response.data.totalCajas);
        setCajasAbiertas(response.data.cajasAbiertas);
        setTotalVentas(response.data.totalVentas);
    } catch (error) {
        console.error('Error al cargar estadísticas:', error);
    }
    }, [filters]);

    // Modificar loadCajas para que NO calcule stats
    const loadCajas = useCallback(async () => {
    setLoading(true);
    try {
        const params: any = {
        page: currentPage,
        limit: 10,
        };

        if (filters.estado) params.estado = filters.estado;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;

        const response = await api.get('/api/cash-register/history', { params });
        setCajas(response.data.cajas);
        setTotalPages(response.data.totalPages);
    } catch (error) {
        console.error('Error al cargar historial:', error);
    } finally {
        setLoading(false);
    }
    }, [currentPage, filters]);

    // Cargar stats por separado
    useEffect(() => {
    loadStats();
    }, [loadStats]);

    useEffect(() => {
    loadCajas();
    }, [loadCajas]);

  const handleViewDetail = async (cajaId: string) => {
    try {
      const response = await api.get(`/api/cash-register/${cajaId}`);
      setSelectedCaja(response.data.caja);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDiferenciaColor = (diferencia?: number) => {
    if (!diferencia) return 'text-gray-500';
    return diferencia >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getDiferenciaIcon = (diferencia?: number) => {
    if (!diferencia) return FaMinus;
    return diferencia >= 0 ? FaArrowUp : FaArrowDown;
  };

  const clearFilters = () => {
    setFilters({ estado: '', startDate: '', endDate: '' });
    setCurrentPage(1);
  };

  if (loading) return <Loader />;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <FaCashRegister className="text-3xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Historial de Cajas</h1>
              <p className="text-blue-100 mt-1">Administra y consulta el historial de cajas registradoras</p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Cajas</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totalCajas}</p>
              <p className="text-sm text-gray-500 mt-1">Registros totales</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-full">
              <FaCashRegister className="text-blue-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Cajas Abiertas</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{cajasAbiertas}</p>
              <p className="text-sm text-gray-500 mt-1">Activas ahora</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="bg-green-100 p-4 rounded-full">
                <FaUser className="text-green-600 text-2xl" />
              </div>
              {cajasAbiertas > 0 && (
                <a
                  href="/caja?action=close"
                  className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                >
                  <i className="fas fa-times-circle"></i>
                  <span>Cerrar</span>
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Ventas</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">${totalVentas.toLocaleString()}</p>
              <p className="text-sm text-gray-500 mt-1">Monto acumulado</p>
            </div>
            <div className="bg-purple-100 p-4 rounded-full">
              <FaMoneyBillWave className="text-purple-600 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Búsqueda
          </h2>
          <button
            onClick={clearFilters}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
          >
            <i className="fa-solid fa-circle-xmark mr-2"></i> Borrar Filtros
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filters.estado}
              onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            >
              <option value="">Todos</option>
              <option value="abierta">Abiertas</option>
              <option value="cerrada">Cerradas</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Inicio</label>
            <div className="relative">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 pr-10"
              />
              <FaCalendarAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin</label>
            <div className="relative">
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 pr-10"
              />
              <FaCalendarAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de Cajas */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        {cajas.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha Apertura
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cajero
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sucursal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto Esperado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto Real
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Diferencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cajas.map((caja) => {
                  const DiferenciaIcon = getDiferenciaIcon(caja.diferencia);
                  return (
                    <tr key={caja._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(caja.fechaApertura)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {caja.cajero.nombre}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {caja.sucursal.nombre}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {formatCurrency(caja.montoEsperado)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {caja.montoReal ? formatCurrency(caja.montoReal) : '-'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${getDiferenciaColor(caja.diferencia)}`}>
                        {caja.diferencia !== undefined ? (
                          <span className="flex items-center">
                            <DiferenciaIcon className="mr-1" />
                            {formatCurrency(Math.abs(caja.diferencia))}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            caja.estado === 'abierta'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {caja.estado === 'abierta' ? 'Abierta' : 'Cerrada'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewDetail(caja._id)}
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-full transition-colors duration-200"
                          title="Ver Detalle"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-2 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaCashRegister className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron cajas</h3>
            <p className="text-gray-500 mb-4">Intenta con otra búsqueda o ajusta los filtros</p>
          </div>
        )}
      </div>

      {/* Paginación */}
      <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-between">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Anterior
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Siguiente
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Página <span className="font-medium">{currentPage}</span> de{' '}
              <span className="font-medium">{totalPages}</span>
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Modal de Detalle */}
      {showDetailModal && selectedCaja && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Detalle de Caja</h3>
                  <p className="text-blue-100">ID: {selectedCaja._id.slice(-8).toUpperCase()}</p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <i className="fas fa-times text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Información General */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Cajero</p>
                  <p className="text-lg font-semibold text-gray-800">{selectedCaja.cajero.nombre}</p>
                  <p className="text-sm text-gray-500">{selectedCaja.cajero.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Sucursal</p>
                  <p className="text-lg font-semibold text-gray-800">{selectedCaja.sucursal.nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fecha Apertura</p>
                  <p className="text-lg font-semibold text-gray-800">{formatDate(selectedCaja.fechaApertura)}</p>
                </div>
                {selectedCaja.fechaCierre && (
                  <div>
                    <p className="text-sm text-gray-600">Fecha Cierre</p>
                    <p className="text-lg font-semibold text-gray-800">{formatDate(selectedCaja.fechaCierre)}</p>
                  </div>
                )}
              </div>

              {/* Resumen Financiero */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-blue-600 font-semibold mb-1">Monto Inicial</p>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(selectedCaja.montoInicial)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-sm text-green-600 font-semibold mb-1">Monto Esperado</p>
                  <p className="text-xl font-bold text-green-700">{formatCurrency(selectedCaja.montoEsperado)}</p>
                </div>
                {selectedCaja.montoReal !== undefined && (
                  <>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <p className="text-sm text-purple-600 font-semibold mb-1">Monto Real</p>
                      <p className="text-xl font-bold text-purple-700">{formatCurrency(selectedCaja.montoReal)}</p>
                    </div>
                    <div className={`rounded-lg p-4 border ${
                      (selectedCaja.diferencia || 0) >= 0
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <p className={`text-sm font-semibold mb-1 ${
                        (selectedCaja.diferencia || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Diferencia
                      </p>
                      <p className={`text-xl font-bold flex items-center ${
                        (selectedCaja.diferencia || 0) >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {React.createElement(getDiferenciaIcon(selectedCaja.diferencia), { className: 'mr-1' })}
                        {formatCurrency(Math.abs(selectedCaja.diferencia || 0))}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Ventas */}
              <div>
                <h4 className="text-lg font-bold text-gray-800 mb-3">Resumen de Ventas</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Total Ventas</p>
                      <p className="text-2xl font-bold text-gray-800">{formatCurrency(selectedCaja.ventas.total)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Cantidad de Ventas</p>
                      <p className="text-2xl font-bold text-gray-800">{selectedCaja.ventas.cantidad}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notas */}
              {selectedCaja.notas && (
                <div>
                  <h4 className="text-lg font-bold text-gray-800 mb-3">Notas</h4>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-gray-700">{selectedCaja.notas}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRegisterHistory;